import {
  computed,
  inject,
  unref,
} from 'vue';
import { Modules } from '@/enum/modules';
import { ElementoBase } from '@/enum/elemento_base';

/**
 * Regla de "ancho de material": el criterio que decide si un input aplica las validaciones
 * y los automatismos ligados al ancho del material del elemento.
 *
 * Estaba duplicada en `input_suaje.vue` e `input_materiales.vue`, con criterios que habían
 * divergido entre sí. Acá queda la definición única; lo que cada componente conserva es su
 * propia lista de módulos y su condición extra, que SÍ son diferencias deliberadas.
 *
 * Lee `currentModule` y `currentElementoBase` del provide/inject que arman los componentes
 * de elemento base de cada módulo.
 */

/** Los cuatro módulos que editan un documento. */
export const MODULOS_DOCUMENTO = [
  Modules.SOLICITUD,
  Modules.COTIZACION,
  Modules.ESPECIFICACION_TECNICA,
  Modules.ORDEN_PRODUCCION,
];

/**
 * Los módulos donde el material ya está definido. Excluye SOLICITUD a propósito: en una
 * solicitud todavía no hay materiales cargados.
 */
export const MODULOS_CON_MATERIAL = [
  Modules.COTIZACION,
  Modules.ESPECIFICACION_TECNICA,
  Modules.ORDEN_PRODUCCION,
];

/** Elementos base cuyo ancho de material se valida contra la imposición. */
export const ELEMENTOS_BASE_ANCHO_MATERIAL = [
  ElementoBase.ETIQUETA,
  ElementoBase.FLEXIBLE,
  ElementoBase.CONSUMIBLE,
  ElementoBase.OFFSET,
];

/**
 * @param {object} opciones
 * @param {number[]} [opciones.modulos] Módulos en los que la regla aplica.
 * @param {number[]} [opciones.elementosBase] Elementos base en los que la regla aplica.
 * @param {import('vue').Ref<boolean>|boolean} [opciones.condicionExtra] Condición adicional
 *   del componente (por ejemplo, que la categoría de material valide el ancho).
 * @returns {import('vue').ComputedRef<boolean>}
 */
export function useAplicaRegla({
  modulos = MODULOS_DOCUMENTO,
  elementosBase = ELEMENTOS_BASE_ANCHO_MATERIAL,
  condicionExtra = true,
} = {}) {
  const currentModule = inject('currentModule', null);
  const currentElementoBase = inject('currentElementoBase', null);

  return computed(() => {
    const moduloOk = modulos.includes(Number(unref(currentModule)));
    const elementoOk = elementosBase.includes(unref(currentElementoBase));

    return moduloOk && elementoOk && Boolean(unref(condicionExtra));
  });
}
